// Centralized GA4 measurement for RapidCare.
// SINGLE delivery route: all GA4 events are sent via direct gtag() ONLY.
// We deliberately never push these events onto window.dataLayer, so the GTM
// container cannot re-forward them and create duplicate GA4 hits.
// (The existing sign_up event is the one intentional exception and is handled
//  inline in Signup.jsx exactly as previously approved.)

const GA_ID = "G-LB8RHX6SCD";

function send(eventName, params = {}) {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  const clean = {};
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") clean[k] = v;
  });
  window.gtag("event", eventName, clean);
}

// Fire at most once per persisted key — used as a SECONDARY front-end safeguard
// for purchase. NOTE: the PRIMARY purchase de-duplication is the unique,
// persistent server-generated transaction_id (PayFast reference) sent with the
// event — GA4/Google Ads de-duplicate revenue conversions by transaction_id.
// localStorage here is only belt-and-braces against rapid double-renders.
function sendOnce(storageKey, eventName, params = {}) {
  try {
    if (localStorage.getItem(storageKey)) return false;
    localStorage.setItem(storageKey, new Date().toISOString());
  } catch (e) {
    /* localStorage blocked — fall through and still send once per page load */
  }
  send(eventName, params);
  return true;
}

// Campaign attribution — utm_source from the current URL, persisted for the
// session so it survives SPA navigation; falls back to the referrer host.
function getCampaignSource() {
  try {
    const utm = new URLSearchParams(window.location.search).get("utm_source");
    if (utm) {
      try { sessionStorage.setItem("rcs_campaign_source", utm); } catch (e) {}
      return utm;
    }
    const stored = sessionStorage.getItem("rcs_campaign_source");
    if (stored) return stored;
    if (document.referrer) {
      try { return new URL(document.referrer).hostname; } catch (e) {}
    }
  } catch (e) {}
  return undefined;
}

// plan-monthly -> monthly, plan-annual -> annual
export function planToSubscriptionType(plan) {
  if (!plan) return undefined;
  if (plan === "plan-monthly" || plan === "monthly" || plan === "month") return "monthly";
  if (plan === "plan-annual" || plan === "annual" || plan === "year") return "annual";
  return plan;
}

export const analytics = {
  // SPA page tracking (config sets send_page_view:false so this is the single source)
  pageView: (path, title) =>
    send("page_view", {
      page_path: path,
      page_title: title || (typeof document !== "undefined" ? document.title : undefined),
      page_location: typeof window !== "undefined" ? window.location.href : undefined,
      send_to: GA_ID,
    }),

  // Returning-user login — NOT a conversion.
  login: (method) => send("login", { method }),

  // Logged-out visitor clicked "Start free trial" (trial intent — top of funnel).
  // Custom event (NOT add_to_cart; nothing is added to a cart). NOT a primary conversion.
  signupStart: (destination = "/signup") =>
    send("signup_start", {
      source_page: typeof window !== "undefined" ? window.location.pathname : undefined,
      campaign_source: getCampaignSource(),
      destination,
      user_status: "logged_out",
    }),

  // Pricing page view.
  viewPricing: (sourcePage, currentPlan) =>
    send("view_pricing", { source_page: sourcePage, current_plan: currentPlan }),

  // User clicked Subscribe and is redirected into PayFast. Secondary conversion.
  beginCheckout: (planType, expectedValue, currency = "ZAR") =>
    send("begin_checkout", {
      plan_type: planType,
      expected_value: expectedValue,
      value: expectedValue,
      currency,
    }),

  // Confirmed successful payment. Secondary conversion. De-duplicated per transaction.
  purchase: (transactionId, value, subscriptionType) =>
    sendOnce(`rcs-ga-purchase-${transactionId || "unknown"}`, "purchase", {
      transaction_id: transactionId,
      value,
      currency: "ZAR",
      subscription_type: subscriptionType,
      payment_provider: "PayFast",
    }),

  // Search performed. search_type: protocol | drug | calculator | ICD10 | interaction | antibiotic guide | global
  search: (searchTerm, searchType) =>
    send("search", { search_term: searchTerm, search_type: searchType }),

  protocolView: (protocolName, protocolCategory) =>
    send("protocol_view", { protocol_name: protocolName, protocol_category: protocolCategory }),

  calculatorUsed: (calculatorName) =>
    send("calculator_used", { calculator_name: calculatorName }),

  // NOTE: never send medication names (patient-identifiable risk).
  drugInteractionCheck: (drugCount, interactionFound) =>
    send("drug_interaction_check", {
      drug_count: drugCount,
      interaction_found: interactionFound,
    }),

  // Diagnostic error events (NOT conversions): signup_failed | login_failed | payment_failed
  error: (eventName, errorType, sourceComponent) =>
    send(eventName, { error_type: errorType, source_component: sourceComponent }),

  // Engagement (recommended GA4 patterns)
  protocolReadTime: (protocolName, seconds) =>
    send("protocol_read_time", {
      protocol_name: protocolName,
      read_seconds: seconds,
      engagement_time_msec: Math.round(seconds * 1000),
    }),

  scrollDepth: (pageName, percent) =>
    send("scroll", { page_name: pageName, percent_scrolled: percent }),
};

export default analytics;
